import { VariableType } from 'json-to-graphql-query';

import { Invoice } from '@/interfaces/invoice';
import { InvoiceGroup } from '@/interfaces/invoiceGroup';
import { InvoiceGroupWithContent } from '@/interfaces/invoiceGroupWithContent';
import { InvoiceFilters } from '../interfaces/filters/invoiceFilters';
import { ListResponse } from '../interfaces/list';
import { EntityResource } from './entity.resource';

/**
 * Resource faktur
 *
 * @export
 * @class InvoiceResource
 * @extends {EntityResource<Invoice>}
 */
export class InvoiceResource extends EntityResource<Invoice> {
  public getFields = {
    id: true,
    number: true,
    price: true,
    priceToPay: true,
    nip: true,
    date: true,
    status: true,
    mdokUid: true,
    description: true
  };

  public listFields = {
    id: true,
    number: true,
    price: true,
    priceToPay: true,
    created: true,
    nip: true,
    date: true,
    status: true,
    mdokUid: true,
    description: true
  };

  public listVariables = { filters: 'InvoiceFilters' };

  /**
   * Lista faktur z danymi dokumentu
   *
   * @param {InvoiceFilters} filters
   * @param {boolean} [preventLoader=false]
   * @return {Promise<ListResponse<Invoice>>}
   * @memberof InvoiceResource
   */
  public listWithDocument(filters: InvoiceFilters, preventLoader = false): Promise<ListResponse<Invoice>> {
    const query = this.prepareOptions({
      query: {
        __variables: this.listVariables,
        [this.listQueryName]: {
          __args: this.listArgs,
          list: { ...this.listFields, document: { id: true, school: true, studentName: true } },
          count: true
        }
      }
    });
    return this.query({ query, variables: { filters } }, this.listQueryName, preventLoader);
  }

  /**
   * Szukanie grup faktur
   *
   * @param {{ documentId: number; search: string }} variables
   * @param {boolean} [preventLoader]
   * @return {Promise<InvoiceGroup[]>}
   * @memberof InvoiceResource
   */
  public searchGroup(
    variables: { documentId: number; search: string },
    preventLoader?: boolean
  ): Promise<InvoiceGroup[]> {
    const query = this.prepareOptions({
      query: {
        __variables: { documentId: 'Int!', search: 'String!' },
        invoiceSearchGroup: {
          __args: { documentId: new VariableType('documentId'), search: new VariableType('search') },
          nip: true,
          number: true,
          date: true,
          price: true
        }
      }
    });

    return this.query({ query, variables }, 'invoiceSearchGroup', preventLoader);
  }

  /**
   * Pobranie danych grupy faktur
   *
   * @param {{ procedureId: number; nip: string; number: string; date: Date }} variables
   * @return {Promise<InvoiceGroupWithContent>}
   * @memberof InvoiceResource
   */
  public getGroup(variables: {
    documentId: number;
    nip: string;
    number: string;
    date: Date;
  }): Promise<InvoiceGroupWithContent> {
    const query = this.prepareOptions({
      query: {
        __variables: { documentId: 'Int!', nip: 'String!', number: 'String!', date: 'DateScalar!' },
        invoiceGetGroup: {
          __args: {
            documentId: new VariableType('documentId'),
            nip: new VariableType('nip'),
            number: new VariableType('number'),
            date: new VariableType('date')
          },
          nip: true,
          number: true,
          date: true,
          price: true,
          priceToPay: true,
          invoices: {
            id: Number,
            school: true,
            documentId: true,
            priceToPay: true
          }
        }
      }
    });
    return this.query({ query, variables }, 'invoiceGetGroup');
  }

  /**
   * Akceptacja faktury
   *
   * @param {number} id
   * @return {Promise<void>}
   * @memberof InvoiceResource
   */
  public approve(variables: { id: number }): Promise<void> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        invoiceApprove: {
          __args: {
            id: new VariableType('id')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'invoiceApprove');
  }

  /**
   * Odblokowanie faktury do edycji
   *
   * @param {number} id
   * @return {Promise<void>}
   * @memberof InvoiceResource
   */
  public unlock(variables: { id: number }): Promise<void> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!' },
        invoiceUnlock: {
          __args: {
            id: new VariableType('id')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'invoiceUnlock');
  }

  constructor() {
    super({
      entityName: 'invoice'
    });
  }
}

export const invoiceResource = new InvoiceResource();
